import { GenericStep } from '@/components/FullWidthTemplate/GenericStep';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import * as Styles from './EnterDomainStep.styles';
import {
  Alert,
  Button,
  IconInfo, SelectField, Skeleton, Tag, TextField, Tooltip,
} from 'gatorcomponents';
import NavBar from '@/media/onboarding/navbar.svg';
import domainValidator from '@/utils/Validators/isValidDomain';

const EnterDomainStep = ({
  domainTlds,
  domainAvailability,
  domainSld,
  setDomainSld,
  domainTld,
  setDomainTld,
  domainIsUnavailable, setDomainIsUnavailable,
  radixIsUnavailable, setRadixIsUnavailable,
  rightButtonLoading,
  documentIsInvalid,
  resetDomainAvailabilityState,
  locales,
  buttonsProps,
}) => {
  const [tldsOptionsList, setTldsOptionsList] = useState([]);
  const [domainValidation, setDomainValidation] = useState(domainValidator.defaultErrors);

  const freeTldsData = useMemo(() => {
    const domainTldsProduct = domainTlds.products && !!domainTlds.products.length && domainTlds.products[0];

    return (domainTldsProduct && !!domainTldsProduct.freedomainTlds && domainTldsProduct.freedomainTlds) || [];
  }, [domainTlds.products]);

  const otherDomainsOptions = domainAvailability.currentDomain.availableTlds.filter(item => freeTldsData.includes(item.tld));

  const currentDomain = `${domainSld}${domainTld.value}`;
  const currentRadix = `${domainSld}.online`;

  const displaySldSkeleton = !domainTlds.data || domainTlds.loading || !tldsOptionsList.length;
  const displayTldSkeleton = !domainTlds.data || domainTlds.loading || !tldsOptionsList.length;
  const hasSomeError = documentIsInvalid
    || domainIsUnavailable
    || Object.values(domainValidation).some(error => error);

  const handleChangeSld = (e) => {
    if (domainAvailability.currentDomain.domain || domainAvailability.currentRadix.domain) {
      resetDomainAvailabilityState();
    }

    setDomainSld(e.target.value);
  };

  const handleChangeTld = ({ label, value }) => {
    if (domainAvailability.currentDomain.domain || domainAvailability.currentRadix.domain) {
      resetDomainAvailabilityState();
    }

    setDomainTld({ label, value });
  };

  const mountOptionsList = useCallback(() => {
    const tldCategories = (domainTlds.data && domainTlds.data.categories && domainTlds.data.categories) || [];
    const tldsList = (domainTlds.data && domainTlds.data.tlds && domainTlds.data.tlds) || [];

    const tldsFormatted = [];

    const mountTldLabel = (tld, isFree) => (
      <Styles.TagLabel>
        {tld}
        {isFree && (
          <Tag label={locales.freeTag} variant="info" size="sm" />
        )}
      </Styles.TagLabel>
    );

    const freeTlds = tldsList.filter(tldItem => freeTldsData.includes(tldItem.tld));
    const freeTldsCategories = freeTlds.map(tldItem => tldItem.categories[0]);

    tldCategories.forEach((category) => {
      if (freeTldsCategories.includes(category)) {
        tldsFormatted.push({ value: category, label: category, title: true });
      }

      freeTlds.forEach((tldItem) => {
        if (!!tldItem.categories.length && tldItem.categories[0] === category) {
          tldsFormatted.push({
            value: tldItem.tld,
            label: mountTldLabel(tldItem.tld, true),
          });
        }
      });
    });

    while (tldsFormatted.length > 0 && tldsFormatted[tldsFormatted.length - 1].title) {
      tldsFormatted.pop();
    }

    if (tldsList.length > 0) {
      const firstTld = tldsFormatted.find(tld => !tld.title);
      setDomainTld(firstTld);
    }

    setTldsOptionsList(tldsFormatted);
  }, [freeTldsData, domainTlds.data, locales.freeTag, setDomainTld]);

  useEffect(() => {
    mountOptionsList();
  }, [mountOptionsList]);

  const validateDomain = useCallback(() => {
    const validateDomain = domainValidator.domainVerification(domainSld, domainTld.value);
    setDomainValidation(validateDomain);
  }, [
    domainSld,
    domainTld,
  ]);

  useEffect(() => {
    validateDomain();
  }, [validateDomain]);

  const checkDomainAvailability = useCallback(() => {
    const domainUnavailable = domainAvailability.status === 'success'
      && domainAvailability.currentDomain.domain === currentDomain
      && !domainAvailability.currentDomain.isAvailable;

    setDomainIsUnavailable(domainUnavailable);
  }, [
    currentDomain,
    setDomainIsUnavailable,
    domainAvailability.status,
    domainAvailability.currentDomain.isAvailable,
    domainAvailability.currentDomain.domain,
  ]);

  useEffect(() => {
    checkDomainAvailability();
  }, [checkDomainAvailability]);

  const checkRadixAvailability = useCallback(() => {
    const radixUnavailable = domainAvailability.status === 'success'
      && domainAvailability.currentRadix.domain === currentRadix
      && !domainAvailability.currentRadix.isAvailable;

    setRadixIsUnavailable(radixUnavailable);
  }, [
    currentRadix,
    setRadixIsUnavailable,
    domainAvailability.status,
    domainAvailability.currentRadix.isAvailable,
    domainAvailability.currentRadix.domain,
  ]);

  useEffect(() => {
    checkRadixAvailability();
  }, [checkRadixAvailability]);

  const mountInputErrorMessage = useCallback(() => {
    if (domainValidation.hasSpecialChars) {
      return locales.inputErrors.hasSpecialChars;
    }
    if (domainValidation.startOrEndWithHyphen) {
      return locales.inputErrors.startOrEndWithHyphen;
    }
    if (domainValidation.twoHyphensInARow) {
      return locales.inputErrors.twoHyphensInARow;
    }
    if (domainValidation.lessThanThreeCharacters) {
      return locales.inputErrors.lessThanThreeCharacters;
    }
    if (domainValidation.invalidDomain) {
      return locales.inputErrors.invalidDomain;
    }
    if (domainIsUnavailable && !!otherDomainsOptions.length) {
      return locales.inputErrors.domainIsUnavailable;
    }
    if (domainIsUnavailable && !otherDomainsOptions.length) {
      return locales.inputErrors.unavailableWithoutChoose;
    }
    if (documentIsInvalid) {
      return locales.inputErrors.documentIsInvalid;
    }

    return '';
  }, [
    domainValidation,
    otherDomainsOptions,
    domainIsUnavailable,
    documentIsInvalid,
    locales,
  ]);

  const inputErrorMessage = mountInputErrorMessage();

  const selectOtherOption = ({ sld, tld }) => {
    const tldOption = tldsOptionsList.find(option => option.value === tld);

    setDomainSld(sld);
    setDomainTld(tldOption);
  };

  const genericStepProps = useCallback(() => ({
    contentWidthFlexible: true,
    buttonsProps: {
      ...buttonsProps,
      rightButton: {
        ...buttonsProps.rightButton,
        disabled: buttonsProps.rightButton.disabled || hasSomeError,
      },
    },
  }), [buttonsProps, hasSomeError]);

  return (
    <GenericStep {...genericStepProps()}>
      <Styles.Wrapper data-testid="enter-domain-step">
        <Styles.Title>{locales.title}</Styles.Title>
        <Styles.Description>
          {locales.description}
          <Styles.TooltipWrapper>
            <Tooltip content={locales.tooltipLabel} allowDynamicFit>
              <IconInfo size="sm" color="lowDark" />
            </Tooltip>
          </Styles.TooltipWrapper>
        </Styles.Description>
        <Styles.NavWrapper>
          <Styles.NavbarContainer>
            <Styles.NavbarImage src={NavBar} />
          </Styles.NavbarContainer>
          <Styles.DomainInputWrapper>
            <Styles.DomainFieldsContainer>
              <Styles.TextFieldContainer>
                {displaySldSkeleton && (
                  <Skeleton width="100%" height="56px" />
                )}
                {!displaySldSkeleton && (
                  <TextField
                    testId="input-sld"
                    label=""
                    onChange={e => handleChangeSld(e)}
                    value={domainSld}
                    status={hasSomeError ? 'error' : 'normal'}
                    disabled={rightButtonLoading}
                  />
                )}
              </Styles.TextFieldContainer>
              <Styles.SelectFieldContainer>
                {displayTldSkeleton && (
                  <Skeleton width="100%" height="56px" />
                )}
                {!displayTldSkeleton && (
                  <SelectField
                    testId="select-tld"
                    withScroll
                    options={tldsOptionsList}
                    placeholder={locales.searchTld}
                    value={domainTld.label}
                    onClickSelect={handleChangeTld}
                    error={domainIsUnavailable || documentIsInvalid}
                    disabled={rightButtonLoading}
                    filterable
                  />
                )}
              </Styles.SelectFieldContainer>
            </Styles.DomainFieldsContainer>

            {inputErrorMessage && (
              <Styles.ErrorText>
                {inputErrorMessage}
              </Styles.ErrorText>
            )}

            {documentIsInvalid && (
              <Styles.InputInstruction>
                {locales.invalidDocumentDescription}
              </Styles.InputInstruction>
            )}
          </Styles.DomainInputWrapper>

          {!domainIsUnavailable && radixIsUnavailable && (
            <Styles.RadixUnavailableMessage>
              <Alert description={locales.radixUnavailable(currentDomain, currentRadix)} centerIcon width="full" />
            </Styles.RadixUnavailableMessage>
          )}

          {domainIsUnavailable && !!otherDomainsOptions.length && (
            <Styles.OtherOptionsWrapper>
              <Styles.OtherOptionsTitle>{locales.otherOptionsLabel}</Styles.OtherOptionsTitle>
              <Styles.OtherOptionsList>
                {otherDomainsOptions.slice(0, 10).map(item => (
                  <Styles.OtherOptionItem key={item.tld}>
                    <Button
                      testId={`other-option-${item.tld}`}
                      variant="secondary"
                      label={item.name}
                      onClick={() => selectOtherOption({ sld: item.sld, tld: item.tld })}
                    />
                  </Styles.OtherOptionItem>
                ))}
              </Styles.OtherOptionsList>
            </Styles.OtherOptionsWrapper>
          )}
        </Styles.NavWrapper>
      </Styles.Wrapper>
    </GenericStep>
  );
};

export default EnterDomainStep;
